import { Icon } from '@/components/ui'
import { Combobox } from './Combobox'
import styles from './fields.module.css'

interface TagsInputProps {
  id: string
  value: string[]
  /** Values already used elsewhere in the site, offered as you type. */
  suggestions?: string[]
  onChange: (next: string[]) => void
}

/**
 * A list of short strings — a tech stack, a set of deliverables, a list of
 * tags — edited as chips rather than as comma-separated text.
 *
 * Comma-separated text looks simpler and is worse: it cannot hold a value
 * containing a comma, it gives no feedback about what will be saved, and a
 * stray separator silently splits one entry into two.
 *
 * ## Suggestions
 *
 * Whatever has been used under the same name anywhere else on the site is
 * offered while typing. Nothing is enforced — these are a record of what
 * exists, not a list of what is allowed — but it is how the seventh project
 * ends up saying "React" rather than starting "ReactJS" and "react" as
 * separate things that no filter will ever reunite.
 *
 * Values already on this field are left out: offering a chip that is already
 * a chip is noise, and adding it again does nothing.
 */
export const TagsInput = ({
  id,
  value,
  suggestions = [],
  onChange,
}: TagsInputProps) => {
  const unused = suggestions.filter((item) => !value.includes(item))

  const add = (tag: string) => {
    const trimmed = tag.trim()
    if (!trimmed) return
    // Silently ignoring a duplicate is friendlier than an error for
    // something the visitor cannot see the consequence of.
    if (!value.includes(trimmed)) onChange([...value, trimmed])
  }

  const remove = (tag: string) => onChange(value.filter((t) => t !== tag))

  return (
    <div className={styles.tags}>
      {value.length > 0 && (
        <ul className={styles.tagList}>
          {value.map((tag) => (
            <li key={tag} className={styles.tag}>
              <span>{tag}</span>
              <button
                type="button"
                onClick={() => remove(tag)}
                className={styles.tagRemove}
                aria-label={`Remove ${tag}`}
              >
                <Icon name="close" size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <Combobox
        id={id}
        value=""
        options={unused.map((item) => ({ value: item, label: item }))}
        allowNew
        placeholder={
          unused.length > 0 ? 'Add one, or pick from what is used' : 'Add one'
        }
        onCommit={add}
        onBackspaceEmpty={() => {
          // What every other chip input does.
          if (value.length > 0) onChange(value.slice(0, -1))
        }}
      />
    </div>
  )
}
